import { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  Save,
  RefreshCw,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  Trash2,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import { Button, Card, ConfirmDialog, Spinner, useToast } from '@centraoneplus/ui';
import { PasswordInput } from '@components/PasswordInput';
import { emailConfigService, type EmailConfig } from '@services/email-config.service';
import { useAuthStore } from '@stores/authStore';
import { useTenantContextStore } from '@stores/tenantContextStore';
import { ThemedSelect } from '@components/common/ThemedSelect';

type Provider = EmailConfig['provider'];
type AuthMode = 'api' | 'smtp';

const PROVIDERS: { value: Provider; label: string }[] = [
  { value: 'SMTP', label: 'SMTP' },
  { value: 'SENDGRID', label: 'SendGrid' },
  { value: 'MAILGUN', label: 'Mailgun' },
  { value: 'ZEPTOMAIL', label: 'ZeptoMail' },
  { value: 'SES', label: 'AWS SES' },
];

const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'eu-west-1', 'eu-west-2', 'eu-central-1',
  'ap-south-1', 'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1',
];

const MASKED_VALUE = '********';

const DEFAULT_CONFIG: EmailConfig = {
  provider: 'SMTP',
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: true,
  smtpUser: '',
  smtpPassword: '',
  sendgridApiKey: '',
  mailgunApiKey: '',
  mailgunDomain: '',
  zeptomailApiKey: '',
  sesAccessKey: '',
  sesSecretKey: '',
  sesRegion: 'us-east-1',
  fromEmail: '',
  fromName: '',
  replyTo: '',
};

const SECRET_FIELDS = ['smtpPassword', 'sendgridApiKey', 'mailgunApiKey', 'zeptomailApiKey', 'sesSecretKey'];

function AuthModeToggle({ mode, onChange, apiLabel = 'API Key', smtpLabel = 'SMTP' }: {
  mode: AuthMode;
  onChange: (mode: AuthMode) => void;
  apiLabel?: string;
  smtpLabel?: string;
}) {
  return (
    <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg mb-4 w-fit">
      <button
        type="button"
        onClick={() => onChange('api')}
        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          mode === 'api'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
      >
        {apiLabel} (Recommended)
      </button>
      <button
        type="button"
        onClick={() => onChange('smtp')}
        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          mode === 'smtp'
            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
      >
        {smtpLabel}
      </button>
    </div>
  );
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

export default function EmailConfigurationSettings() {
  const toast = useToast();
  const { user } = useAuthStore();
  const { currentTenant } = useTenantContextStore();
  const tenantId = currentTenant?.id || user?.tenantId;

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isNewConfig, setIsNewConfig] = useState(true);

  const [config, setConfig] = useState<EmailConfig>({ ...DEFAULT_CONFIG });
  const [originalConfig, setOriginalConfig] = useState<EmailConfig | null>(null);
  const [testEmail, setTestEmail] = useState(user?.email || '');

  // Track which secret fields were masked (shouldn't be sent back as-is)
  const [maskedFields, setMaskedFields] = useState<Set<string>>(new Set());

  // A saved-but-paused config: kept in full, simply not used for sending.
  const isPaused = !isNewConfig && config.isActive === false;

  // Auth mode toggles for providers that support both
  const [mailgunMode, setMailgunMode] = useState<AuthMode>('api');
  const [zeptoMode, setZeptoMode] = useState<AuthMode>('api');
  const [sesMode, setSesMode] = useState<AuthMode>('api');

  const detectMaskedFields = useCallback((data: EmailConfig): Set<string> => {
    const masked = new Set<string>();
    for (const field of SECRET_FIELDS) {
      if ((data as any)[field] === MASKED_VALUE) masked.add(field);
    }
    return masked;
  }, []);

  const loadConfig = useCallback(async () => {
    if (!tenantId) return;
    try {
      setIsLoading(true);
      const data = await emailConfigService.getConfig();
      if (data) {
        setConfig(data);
        setOriginalConfig(data);
        setIsNewConfig(false);
        setMaskedFields(detectMaskedFields(data));

        // Infer auth modes from populated fields
        if (data.mailgunApiKey || data.mailgunApiKey === MASKED_VALUE) {
          setMailgunMode('api');
        } else if (data.smtpUser || data.smtpPassword === MASKED_VALUE) {
          setMailgunMode('smtp');
        }
        if (data.zeptomailApiKey || data.zeptomailApiKey === MASKED_VALUE) {
          setZeptoMode('api');
        } else if (data.smtpPassword === MASKED_VALUE) {
          setZeptoMode('smtp');
        }
        if (data.sesAccessKey || data.sesSecretKey === MASKED_VALUE) {
          setSesMode('api');
        } else if (data.smtpUser) {
          setSesMode('smtp');
        }
      }
    } catch (error) {
      console.error('Error loading email config:', error);
      toast.error(
        'Failed to load email configuration',
        (error as any)?.response?.data?.message ||
          (error as any)?.response?.data?.error?.message ||
          (error as any)?.message ||
          'Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, detectMaskedFields]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (originalConfig) {
      setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    } else {
      // New config — has changes if required fields are filled
      setHasChanges(!!config.fromEmail && !!config.fromName);
    }
  }, [config, originalConfig]);

  const updateField = <K extends keyof EmailConfig>(field: K, value: EmailConfig[K]) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    // If user edits a masked field, it's no longer masked
    if (maskedFields.has(field as string)) {
      setMaskedFields((prev) => {
        const next = new Set(prev);
        next.delete(field as string);
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (!config.fromEmail || !config.fromName) {
      toast.error('From Email and From Name are required');
      return;
    }

    try {
      setIsSaving(true);

      // Don't send masked values back to the server
      const payload: Record<string, any> = { ...config };
      for (const field of maskedFields) {
        delete payload[field];
      }
      // isActive is owned by the pause/resume endpoint, never by this PUT.
      delete payload.isActive;

      const updated = await emailConfigService.updateConfig(payload as Partial<EmailConfig>);
      setConfig(updated);
      setOriginalConfig(updated);
      setIsNewConfig(false);
      setHasChanges(false);
      setMaskedFields(detectMaskedFields(updated));

      toast.success('Email configuration saved');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save email configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address for the test');
      return;
    }
    if (isNewConfig || hasChanges) {
      toast.error('Please save your configuration first before testing');
      return;
    }

    try {
      setIsTesting(true);
      const result = await emailConfigService.testConfig(testEmail);
      if (result.success) {
        toast.success(`Test email sent to ${testEmail}`);
      } else {
        toast.error(result.error || 'Failed to send test email');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setIsTesting(false);
    }
  };

  const handleToggleActive = async () => {
    const next = isPaused;
    try {
      setIsPausing(true);
      const updated = await emailConfigService.setActive(next);
      setConfig(updated);
      setOriginalConfig(updated);
      setHasChanges(false);
      setMaskedFields(detectMaskedFields(updated));
      toast.success(
        next
          ? 'Email configuration resumed'
          : 'Email configuration paused - sending falls back to the default provider',
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update email configuration');
    } finally {
      setIsPausing(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await emailConfigService.deleteConfig();

      // Back to the "not configured yet" state. Mail keeps flowing: the backend
      // falls back to the system email config, so this only clears the
      // organization-specific sender.
      setConfig({ ...DEFAULT_CONFIG });
      setOriginalConfig(null);
      setIsNewConfig(true);
      setMaskedFields(new Set());
      setHasChanges(false);
      setShowDeleteConfirm(false);

      toast.success('Email configuration deleted');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete email configuration');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReset = () => {
    if (originalConfig) {
      setConfig(originalConfig);
    } else {
      setConfig({ ...DEFAULT_CONFIG });
    }
    setHasChanges(false);
  };

  // Reusable input renderers
  const renderPasswordInput = (field: keyof EmailConfig, label: string, placeholder: string) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      <PasswordInput
        value={maskedFields.has(field) ? '' : ((config[field] as string) || '')}
        onChange={(e) => updateField(field, e.target.value as any)}
        placeholder={maskedFields.has(field) ? '(saved - enter new to change)' : placeholder}
        className={inputClass}
      />
    </div>
  );

  const renderTextInput = (field: keyof EmailConfig, label: string, placeholder: string) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      <input
        type="text"
        value={(config[field] as string) || ''}
        onChange={(e) => updateField(field, e.target.value as any)}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure the outbound email provider used for notifications, invitations, and system emails.
        </p>
      </div>

      {isNewConfig && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">No email configuration found</p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              System emails (invitations, notifications) will not be sent until you configure an email provider below.
            </p>
          </div>
        </div>
      )}

      {isPaused && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <PauseCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              This email configuration is paused
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Your settings are saved but not in use. Outgoing email falls back to the platform's
              default provider, so notifications, invitations and password resets keep working.
              Switch it back on at any time.
            </p>
          </div>
        </div>
      )}

      {/* Provider Selection */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Email Provider</h3>
        <div className="grid grid-cols-5 gap-2">
          {PROVIDERS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => updateField('provider', p.value)}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors ${
                config.provider === p.value
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Provider-specific fields */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
          {config.provider === 'SMTP' ? 'SMTP Settings' :
           config.provider === 'SENDGRID' ? 'SendGrid Settings' :
           config.provider === 'MAILGUN' ? 'Mailgun Settings' :
           config.provider === 'ZEPTOMAIL' ? 'ZeptoMail Settings' :
           'AWS SES Settings'}
        </h3>

        <div className="space-y-4">
          {/* SMTP */}
          {config.provider === 'SMTP' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                {renderTextInput('smtpHost', 'SMTP Host', 'smtp.gmail.com')}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP Port</label>
                  <input
                    type="number"
                    value={config.smtpPort || 587}
                    onChange={(e) => updateField('smtpPort', parseInt(e.target.value) || 587)}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {renderTextInput('smtpUser', 'Username', 'user@example.com')}
                {renderPasswordInput('smtpPassword', 'Password', 'App password or SMTP password')}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="smtpSecure"
                  checked={config.smtpSecure ?? true}
                  onChange={(e) => updateField('smtpSecure', e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-700"
                />
                <label htmlFor="smtpSecure" className="text-sm text-gray-700 dark:text-gray-300">
                  Use TLS/SSL
                </label>
              </div>
            </>
          )}

          {/* SendGrid */}
          {config.provider === 'SENDGRID' && (
            renderPasswordInput('sendgridApiKey', 'SendGrid API Key', 'SG.xxxxxxxxxxxx')
          )}

          {/* Mailgun */}
          {config.provider === 'MAILGUN' && (
            <>
              <AuthModeToggle mode={mailgunMode} onChange={setMailgunMode} apiLabel="API Key" smtpLabel="SMTP" />
              {mailgunMode === 'api' ? (
                <div className="grid grid-cols-2 gap-4">
                  {renderPasswordInput('mailgunApiKey', 'Mailgun API Key', 'key-xxxxxxxxxxxx')}
                  {renderTextInput('mailgunDomain', 'Mailgun Domain', 'mg.yourdomain.com')}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {renderTextInput('smtpUser', 'SMTP Username', 'postmaster@mg.yourdomain.com')}
                  {renderPasswordInput('smtpPassword', 'SMTP Password', 'Mailgun SMTP password')}
                </div>
              )}
            </>
          )}

          {/* ZeptoMail */}
          {config.provider === 'ZEPTOMAIL' && (
            <>
              <AuthModeToggle mode={zeptoMode} onChange={setZeptoMode} apiLabel="API Key" smtpLabel="SMTP Token" />
              {zeptoMode === 'api' ? (
                <div>
                  {renderPasswordInput('zeptomailApiKey', 'ZeptoMail API Key', 'Enter your ZeptoMail API Key')}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Generate an API key from your ZeptoMail console (Settings → API Keys)
                  </p>
                </div>
              ) : (
                <div>
                  {renderPasswordInput('smtpPassword', 'Send Mail Token', 'Enter your ZeptoMail Send Mail Token')}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Generate a Send Mail Token from your ZeptoMail console (Mail Agents → Setup Info)
                  </p>
                </div>
              )}
            </>
          )}

          {/* AWS SES */}
          {config.provider === 'SES' && (
            <>
              <AuthModeToggle mode={sesMode} onChange={setSesMode} apiLabel="AWS Credentials" smtpLabel="SMTP" />
              {sesMode === 'api' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {renderTextInput('sesAccessKey', 'Access Key ID', 'AKIAIOSFODNN7EXAMPLE')}
                    {renderPasswordInput('sesSecretKey', 'Secret Access Key', 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY')}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">AWS Region</label>
                    <ThemedSelect
                      value={config.sesRegion || 'us-east-1'}
                      onChange={(e) => updateField('sesRegion', e.target.value)}
                      className={inputClass}
                    >
                      {AWS_REGIONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </ThemedSelect>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Create an IAM user with SES send permissions. Use its Access Key and Secret Key.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {renderTextInput('smtpUser', 'SMTP Username', 'SES SMTP username')}
                    {renderPasswordInput('smtpPassword', 'SMTP Password', 'SES SMTP password')}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">AWS Region</label>
                    <ThemedSelect
                      value={config.sesRegion || 'us-east-1'}
                      onChange={(e) => updateField('sesRegion', e.target.value)}
                      className={inputClass}
                    >
                      {AWS_REGIONS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </ThemedSelect>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Generate SMTP credentials from AWS SES console (SMTP Settings → Create SMTP credentials).
                    SMTP host is auto-derived from the selected region.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Common fields */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Sender Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={config.fromEmail || ''}
                onChange={(e) => updateField('fromEmail', e.target.value)}
                placeholder="noreply@yourcompany.com"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                From Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={config.fromName || ''}
                onChange={(e) => updateField('fromName', e.target.value)}
                placeholder="CentraOnePlus"
                className={inputClass}
              />
            </div>
          </div>
          <div className="w-[calc(50%-0.5rem)]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reply-To Email</label>
            <input
              type="email"
              value={config.replyTo || ''}
              onChange={(e) => updateField('replyTo', e.target.value)}
              placeholder="support@yourcompany.com (optional)"
              className={inputClass}
            />
          </div>
        </div>
      </Card>

      {/* Test Email - hidden while paused: a test would send via the fallback
          provider, not the settings shown on screen. */}
      {!isNewConfig && !hasChanges && !isPaused && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Test Configuration</h3>
          <div className="flex items-end gap-3">
            <div className="w-[calc(50%-0.5rem)] flex-none">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Send test email to</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className={inputClass}
              />
            </div>
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isTesting || !testEmail}
            >
              {isTesting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Test
            </Button>
          </div>
        </Card>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          {!isNewConfig && (
            <Button
              variant="outline"
              onClick={handleToggleActive}
              disabled={isSaving || isDeleting || isPausing}
            >
              {isPausing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : isPaused ? (
                <PlayCircle className="h-4 w-4 mr-2" />
              ) : (
                <PauseCircle className="h-4 w-4 mr-2" />
              )}
              {isPaused ? 'Turn On' : 'Turn Off'}
            </Button>
          )}
          {!isNewConfig && (
            <Button
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSaving || isDeleting || isPausing}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Configuration
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset} disabled={isSaving || isDeleting}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || isDeleting || !config.fromEmail || !config.fromName}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isNewConfig ? 'Save Configuration' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        variant="danger"
        title="Delete email configuration?"
        message={
          `This removes the ${PROVIDERS.find((p) => p.value === config.provider)?.label ?? ''} settings and ` +
          `sender details for this organization, including any stored API key or password. ` +
          `Email will keep sending using the platform's default provider until you configure one again. ` +
          `This cannot be undone.`
        }
        confirmLabel="Delete Configuration"
        cancelLabel="Cancel"
      />
    </div>
  );
}
